import React, { useEffect, useState } from "react";
import { Subheading } from "../../../design/heading";
import { Divider } from "../../../design/divider";
import { Link } from "../../../design/link";
import { Badge } from "../../../design/badge";
import { CaseAttorney, ATTORNEY_ROLE_LABELS } from "@visitproof/types";
import { useCase } from "../../../contexts/CaseContext";
import AttorneyService from "../../../services/AttorneyService";
import { useAccount } from "../../../contexts/AccountContext";

interface Props {
  attorneyId: string;
}

const AttorneyCasesList: React.FC<Props> = ({ attorneyId }) => {
  const { account } = useAccount();
  const { cases } = useCase();
  const [caseAttorneys, setCaseAttorneys] = useState<CaseAttorney[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCaseAttorneys = async () => {
      if (!account?.id) return;
      try {
        const assignments = await AttorneyService.getAttorneyCases(
          account.id,
          attorneyId
        );
        setCaseAttorneys(assignments);
      } catch (error) {
        console.error("Error loading attorney cases:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCaseAttorneys();
  }, [account?.id, attorneyId]);

  const getCaseDetails = (caseId: string) => {
    return cases.find((c) => c.id === caseId);
  };

  if (loading) {
    return (
      <>
        <Subheading>Assigned Cases</Subheading>
        <Divider className="my-4" />
        <p className="text-sm text-zinc-500">Loading...</p>
      </>
    );
  }

  const activeCases = caseAttorneys.filter((ca) => ca.isActive);
  const inactiveCases = caseAttorneys.filter((ca) => !ca.isActive);

  return (
    <>
      <Subheading>
        Assigned Cases{" "}
        <span className="text-zinc-500">({activeCases.length} active)</span>
      </Subheading>
      <Divider className="my-4" />

      {caseAttorneys.length === 0 ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          This attorney is not assigned to any cases.
        </p>
      ) : (
        <div className="space-y-3">
          {activeCases.map((ca) => {
            const caseDetails = getCaseDetails(ca.caseId);
            if (!caseDetails) return null;
            return (
              <Link
                key={ca.id}
                href={`/case/${ca.caseId}`}
                className="flex items-center justify-between p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
              >
                <div>
                  <p className="font-medium text-zinc-900 dark:text-white">
                    {caseDetails.identifier || `Case ${caseDetails.id.slice(0, 8)}`}
                  </p>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    {ATTORNEY_ROLE_LABELS[ca.role]}
                    {ca.roleLabel && ` - ${ca.roleLabel}`}
                  </p>
                </div>
                <Badge color="green">Active</Badge>
              </Link>
            );
          })}

          {inactiveCases.length > 0 && (
            <>
              <Divider className="my-4" />
              <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-2">
                Past Assignments
              </p>
              {inactiveCases.map((ca) => {
                const caseDetails = getCaseDetails(ca.caseId);
                if (!caseDetails) return null;
                return (
                  <Link
                    key={ca.id}
                    href={`/case/${ca.caseId}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors opacity-60"
                  >
                    <div>
                      <p className="font-medium text-zinc-900 dark:text-white">
                        {caseDetails.identifier || `Case ${caseDetails.id.slice(0, 8)}`}
                      </p>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400">
                        {ATTORNEY_ROLE_LABELS[ca.role]}
                      </p>
                    </div>
                    <Badge color="zinc">Inactive</Badge>
                  </Link>
                );
              })}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default AttorneyCasesList;
